"use client";

import { useCallback, useRef, useState } from "react";
import { AlertCircle, FileUp, Loader2 } from "lucide-react";

export const INTERVIEW_TYPES = [
  { id: "general", labelKey: "type_general" },
  { id: "behavioral", labelKey: "type_behavioral" },
  { id: "technical", labelKey: "type_technical" },
  { id: "role_specific", labelKey: "type_role_specific" },
];

export const EXPERIENCE_LEVELS = [
  { id: "", labelKey: "exp_unspecified" },
  { id: "entry", labelKey: "exp_entry" },
  { id: "mid", labelKey: "exp_mid" },
  { id: "senior", labelKey: "exp_senior" },
  { id: "lead", labelKey: "exp_lead" },
];

/** Minimum job-description length considered "sufficient role context". */
export const MIN_JD_LENGTH = 20;

/**
 * STEP 2 — Target Job.
 *
 * Collects target job title, optional company, job description (paste or
 * file upload), interview type, optional experience level and focus areas.
 */
export default function TargetJobStep({ t, value, onChange }) {
  const fileInputRef = useRef(null);
  const [readingFile, setReadingFile] = useState(false);
  const [fileError, setFileError] = useState(null);

  const patch = useCallback(
    (fields) => onChange({ ...value, ...fields }),
    [onChange, value]
  );

  const handleJdFile = useCallback(
    async (file) => {
      if (!file) return;
      setFileError(null);
      setReadingFile(true);
      try {
        const { extractResumeText } = await import(
          "@/utils/extractResumeText"
        );
        const text = await extractResumeText(file);
        if (!text) {
          setFileError(t.jd_file_error);
        } else {
          patch({ description: text.slice(0, 20000) });
        }
      } catch {
        setFileError(t.jd_file_error);
      } finally {
        setReadingFile(false);
      }
    },
    [patch, t]
  );

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-slate-600">
            {t.job_title_label} *
          </span>
          <input
            type="text"
            value={value.title}
            onChange={(e) => patch({ title: e.target.value })}
            placeholder={t.job_title_placeholder}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-slate-600">
            {t.company_label}
          </span>
          <input
            type="text"
            value={value.company}
            onChange={(e) => patch({ company: e.target.value })}
            placeholder={t.company_placeholder}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          />
        </label>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-medium text-slate-600">
            {t.jd_label} *
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(e) => {
              handleJdFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={readingFile}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            {readingFile ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileUp className="h-3.5 w-3.5" />
            )}
            {t.jd_upload_btn}
          </button>
        </div>
        <textarea
          value={value.description}
          onChange={(e) => patch({ description: e.target.value })}
          placeholder={t.jd_placeholder}
          rows={6}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        />
        <p className="mt-1 text-xs text-slate-500">{t.jd_min_hint}</p>
        {fileError && (
          <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
            <AlertCircle className="h-3.5 w-3.5" />
            {fileError}
          </p>
        )}
      </div>

      <div>
        <span className="mb-1.5 block text-xs font-medium text-slate-600">
          {t.interview_type_label}
        </span>
        <div className="flex flex-wrap gap-2">
          {INTERVIEW_TYPES.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => patch({ interviewType: type.id })}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition ${
                value.interviewType === type.id
                  ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
            >
              {t[type.labelKey]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-slate-600">
            {t.experience_label}
          </span>
          <select
            value={value.experienceLevel}
            onChange={(e) => patch({ experienceLevel: e.target.value })}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          >
            {EXPERIENCE_LEVELS.map((level) => (
              <option key={level.id} value={level.id}>
                {t[level.labelKey]}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-slate-600">
            {t.focus_label}
          </span>
          <input
            type="text"
            value={value.focusAreas}
            onChange={(e) => patch({ focusAreas: e.target.value })}
            placeholder={t.focus_placeholder}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          />
        </label>
      </div>
    </div>
  );
}

